import { InfiniteScroll, Popup, Radio, SearchBar, Skeleton, Toast } from "antd-mobile";
import { DownOutline, RightOutline } from "antd-mobile-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getQualityDashboard,
  getQualityRecords,
  MobileQualityDashboard,
  MobileQualityRecord,
  QualityQueue,
} from "../../api/mobileQuality";
import request from "../../axios";
import { queueMeta } from "../../utils/mobileQuality";
import styles from "./index.module.css";

interface SelectorItem {
  id: string;
  name: string;
}

const emptyDashboard: MobileQualityDashboard = {
  date: dayjs().format("YYYY-MM-DD"),
  capturedCount: 0,
  evaluatedCount: 0,
  qualifiedCount: 0,
  unqualifiedCount: 0,
  captureIssueCount: 0,
  pendingReviewCount: 0,
  qualifiedRate: 0,
};

const queueCount = (dashboard: MobileQualityDashboard, queue: QualityQueue) => ({
  UNQUALIFIED: dashboard.unqualifiedCount,
  QUALIFIED: dashboard.qualifiedCount,
  CAPTURE_ISSUE: dashboard.captureIssueCount,
  PENDING_REVIEW: dashboard.pendingReviewCount,
}[queue]);

const Page = () => {
  const navigate = useNavigate();
  const date = dayjs().format("YYYY-MM-DD");
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [queue, setQueue] = useState<QualityQueue>("UNQUALIFIED");
  const [records, setRecords] = useState<MobileQualityRecord[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<SelectorItem[]>([]);
  const [dishes, setDishes] = useState<SelectorItem[]>([]);
  const [storeId, setStoreId] = useState("");
  const [dishId, setDishId] = useState("");
  const [storePopup, setStorePopup] = useState(false);
  const [dishPopup, setDishPopup] = useState(false);
  const [dishKeyword, setDishKeyword] = useState("");

  const selectedStoreName = stores.find((item) => String(item.id) === storeId)?.name || "全部门店";
  const selectedDishName = dishes.find((item) => String(item.id) === dishId)?.name || "全部菜品";
  const filteredDishes = useMemo(
    () => dishes.filter((item) => item.name.includes(dishKeyword.trim())),
    [dishes, dishKeyword]
  );

  useEffect(() => {
    Promise.all([
      request.get("/store/list", { pageNo: 1, pageSize: 999 }),
      request.get("/dish/list", { pageNo: 1, pageSize: 999 }),
    ]).then(([storeResponse, dishResponse]) => {
      setStores(storeResponse.data?.records || []);
      setDishes(dishResponse.data?.records || []);
    }).catch(() => Toast.show({ content: "筛选数据加载失败" }));
  }, []);

  useEffect(() => {
    getQualityDashboard({ date, storeId, dishId })
      .then(setDashboard)
      .catch(() => Toast.show({ content: "品质概览加载失败" }));
  }, [date, storeId, dishId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPage(1);
    getQualityRecords({ date, queue, page: 1, pageSize: 20, storeId, dishId })
      .then((result) => {
        if (!active) return;
        setRecords(result.records || []);
        setHasMore((result.current || 1) < (result.pages || 0));
      })
      .catch(() => active && Toast.show({ content: "品质记录加载失败" }))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [date, queue, storeId, dishId]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    const result = await getQualityRecords({ date, queue, page: nextPage, pageSize: 20, storeId, dishId });
    setRecords((current) => [...current, ...(result.records || [])]);
    setPage(nextPage);
    setHasMore(nextPage < (result.pages || 0));
  };

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div>
          <span>{dayjs().format("YYYY年M月D日")}</span>
          <h1>今日品质</h1>
        </div>
        <button className={styles.storeButton} onClick={() => setStorePopup(true)}>
          <span>{selectedStoreName}</span><DownOutline />
        </button>
      </header>

      <section className={styles.summary}>
        <div className={styles.rateBlock}>
          <span>品质达标率</span>
          <strong>{dashboard.qualifiedRate.toFixed(1)}%</strong>
          <small>{dashboard.qualifiedCount} 达标 / {dashboard.evaluatedCount} 已评分</small>
        </div>
        <div className={styles.summaryStats}>
          <div><b>{dashboard.capturedCount}</b><span>今日出餐</span></div>
          <div><b>{dashboard.evaluatedCount}</b><span>已评分</span></div>
        </div>
      </section>

      <section className={styles.queues} aria-label="品质队列">
        {(Object.keys(queueMeta) as QualityQueue[]).map((item) => {
          const meta = queueMeta[item];
          return (
            <button
              key={item}
              className={`${styles.queueCard} ${styles[meta.tone]} ${queue === item ? styles.active : ""}`}
              onClick={() => setQueue(item)}
            >
              <span>{meta.label}</span>
              <strong>{queueCount(dashboard, item)}</strong>
              <small>{meta.description}</small>
            </button>
          );
        })}
      </section>

      <section className={styles.recordsSection}>
        <div className={styles.filters}>
          <button onClick={() => setStorePopup(true)}>{selectedStoreName}<DownOutline /></button>
          <button onClick={() => setDishPopup(true)}>{selectedDishName}<DownOutline /></button>
        </div>
        <div className={styles.sectionTitle}>
          <h2>{queueMeta[queue].label}记录</h2>
          <span>按出餐时间倒序</span>
        </div>

        {loading ? <Skeleton.Paragraph lineCount={5} animated /> : records.length === 0 ? (
          <div className={styles.empty}>当前筛选条件下暂无记录</div>
        ) : (
          <div className={styles.recordList}>
            {records.map((record) => (
              <button key={record.id} className={styles.record} onClick={() => navigate(`/quality/${record.id}`)}>
                <img src={record.captureImageUrl || ""} alt={`${record.dishName || "菜品"}实拍`} />
                <span className={styles.recordCopy}>
                  <strong>{record.dishName || (record.captureStatus === "INVALID_CAPTURE" ? "无效拍摄" : "待识别菜品")}</strong>
                  <small>{record.storeName || "-"}</small>
                  <em className={styles[queueMeta[queue].tone]}>
                    {record.qualityResult === "QUALIFIED" ? "达标" : record.qualityResult === "UNQUALIFIED" ? "不达标" : "拍摄问题"}
                    {record.aiReason ? ` · ${record.aiReason}` : ""}
                  </em>
                </span>
                <span className={styles.recordMeta}>
                  <time>{record.capturedAt ? dayjs(record.capturedAt).format("HH:mm") : "-"}</time>
                  <RightOutline />
                </span>
              </button>
            ))}
            <InfiniteScroll loadMore={loadMore} hasMore={hasMore} />
          </div>
        )}
      </section>

      <Popup visible={storePopup} onMaskClick={() => setStorePopup(false)} bodyClassName={styles.popup}>
        <h2>选择查看门店</h2>
        <Radio.Group value={storeId} onChange={(value) => { setStoreId(String(value)); setStorePopup(false); }}>
          <Radio className={styles.choice} value="">全部门店</Radio>
          {stores.map((item) => <Radio className={styles.choice} key={item.id} value={String(item.id)}>{item.name}</Radio>)}
        </Radio.Group>
      </Popup>

      <Popup visible={dishPopup} onMaskClick={() => setDishPopup(false)} bodyClassName={styles.popup}>
        <h2>选择菜品</h2>
        <SearchBar value={dishKeyword} onChange={setDishKeyword} placeholder="搜索菜名" />
        <Radio.Group value={dishId} onChange={(value) => { setDishId(String(value)); setDishPopup(false); }}>
          <Radio className={styles.choice} value="">全部菜品</Radio>
          {filteredDishes.map((item) => <Radio className={styles.choice} key={item.id} value={String(item.id)}>{item.name}</Radio>)}
        </Radio.Group>
      </Popup>
    </main>
  );
};

export default Page;
